import {useFormik} from 'formik';
import {object, string} from 'yup';
import {FC, useContext, useEffect} from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import moment from 'moment/moment';
import AuthContext from '../../../contexts/AuthContext';
import SavAPI from '../../../services/SavAPI';
import {useStoreSavs} from '../../../store/useStoreSavs';
import Field from '../../ui/form/Field';
import AlertCustom from '../../ui/alert/AlertCustom';

type SavFormProps = {
    sav: null | Array<object>
    setShowModal: FC
    setModalContent: FC;
}

export const SavForm = ({sav, setShowModal, setModalContent}: SavFormProps) => {
    const {user} = useContext(AuthContext);
    const claimants = useStoreSavs((state) => state.claimants);
    const fetchClaimants = useStoreSavs((state) => state.fetchClaimants);
    const fetchReasons = useStoreSavs((state) => state.fetchReasons);
    const reasons = useStoreSavs((state) => state.reasons);

    async function getSav(sav) {
        await SavAPI.getSav(sav).then(({data}) => {
            return data;
        })
    }

    if (sav?.[1]) {
        const result = getSav(sav[1])
        const motifId = reasons.filter(
            (reason) => reason.motifDesc === result.data.reclaMotif,
        )

        sav = {
            reclaCommande: sav[0],
            reclaDesc: result.data.reclaTxtplaignant,
            reclaReclamant: result.data.reclaPlaignant,
            reclaMotif: motifId[0].motifIdx,
            type: 'update',
        }
    } else {
        sav = {
            reclaCommande: sav[0],
            type: 'create',
        }
    }

    const formik = useFormik({
        initialValues: {
            reclaCommande: sav.reclaCommande,
            reclaDesc: sav.reclaDesc || '',
            reclaReclamant: sav.reclaReclamant || 'default',
            reclaMotif: sav.reclaMotif || 'default',
        },
        enableReinitialize: true,
        validationSchema: object().shape({
            reclaReclamant: string().matches(
                /^(?!default$).*/,
                '* Ce champ est requis.',
            ),
            reclaMotif: string().matches(
                /^(?!default$).*/,
                '* Ce champ est requis.',
            ),
            reclaDesc: string()
                .min(2, '* Ce champ doit contenir au minimum 2 caractères')
                .required('* Ce champ est requis.'),
        }),
        onSubmit: (values) => {
            const data = {
                reclaCommande: values.reclaCommande,
                reclaDesc: values.reclaDesc,
                reclaMotif: parseInt(values.reclaMotif, 10),
                reclaReclamant: parseInt(values.reclaReclamant, 10),
            }
            postSav(data);
        },
    });

    useEffect(() => {
        fetchClaimants();
        fetchReasons();
    }, [])

    async function postSav(values) {
        try {
            await SavAPI.postSav(user.CodeFl, sav.type, values)
            setShowModal(false)
        } catch (error) {
            setModalContent(
                <AlertCustom
                    variant={'danger'}
                    heading={true}
                    bodyContent={error.response.data.detail}
                    transition={true}
                />,
            )
        }
    }

    return (
        <Col>
            <Form
                onSubmit={(event) => {
                    setShowModal(true)
                    event.preventDefault()
                    formik.handleSubmit()
                }}
                className={'mb-3'}
                id={'savForm'}>
                <Row className={'mb-3'}>
                    <Col>N° commande : {sav.reclaCommande}</Col>
                    <Col>
                        Date d'ouverture :{' '}
                        {moment(new Date()).format('DD/MM/YYYY')}
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group
                            controlId={'reclaReclamant'}
                            className={'mb-3'}>
                            <Form.Label>
                                Réclamant{' '}
                                <span className={'text-red'}>*</span>
                            </Form.Label>
                            <Form.Control
                                as={'select'}
                                className={
                                    (formik.touched.reclaReclamant &&
                                        formik.errors.reclaReclamant &&
                                        'is-invalid') ||
                                    (formik.touched.reclaReclamant &&
                                        !formik.errors.reclaReclamant &&
                                        'is-valid')
                                }
                                value={formik.values.reclaReclamant}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}>
                                <option value={'default'}>
                                    Sélectionner un réclamant
                                </option>
                                {claimants?.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId={'reclaMotif'} className={'mb-3'}>
                            <Form.Label>
                                Motif<span className={'text-red'}> *</span>
                            </Form.Label>
                            <Form.Control
                                as={'select'}
                                className={
                                    (formik.touched.reclaMotif &&
                                        formik.errors.reclaMotif &&
                                        'is-invalid') ||
                                    (formik.touched.reclaMotif &&
                                        !formik.errors.reclaMotif &&
                                        'is-valid')
                                }
                                value={formik.values.reclaMotif}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}>
                                <option value={'default'}>
                                    Sélectionner un motif
                                </option>
                                {reasons.map((item, index) => (
                                    <option key={index} value={item.motifIdx}>
                                        {item.motifDesc}
                                    </option>
                                ))}
                            </Form.Control>
                            {formik.touched.reclaMotif &&
                                formik.errors.reclaMotif && (
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.reclaMotif}
                                    </Form.Control.Feedback>
                                )}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Field
                            as={'textarea'}
                            rows={10}
                            label={'Description du SAV'}
                            labelRequired={true}
                            name={'reclaDesc'}
                            value={formik.values.reclaDesc}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder={'Entrez votre description'}
                            classNames={'mb-3'}
                            error={
                                formik.touched.reclaDesc &&
                                formik.errors.reclaDesc
                            }
                            success={
                                formik.touched.reclaDesc &&
                                !formik.errors.reclaDesc
                            }
                        />
                    </Col>
                </Row>
            </Form>
        </Col>
    )
}
